/* eslint-disable id-length */
import { Outlet, useMatches } from "react-router-dom";
import { useCallback, useEffect, useRef } from "react";
import PausedClusterQueuesNotice from "~/components/shared/PausedClusterQueuesNotice";
import BuildContentNavigation from "~/components/build/Show/components/BuildContentNavigation";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import Header from "~/components/build/Header";
import Sidebar from "~/components/build/Show/components/Sidebar";
import { PipelineInfo } from "~/components/build/Header/PipelineInfo";
import { PipelineSettingsMenu } from "~/components/build/Header/PipelineSettingsMenu";
import { Branch } from "~/components/build/Header/Branch";
import { Commit } from "~/components/build/Header/Commit";
import BuildDuration from "~/components/build/Duration";
import PipelineStateIcon, {
  VARIANTS,
} from "~/components/shared/PipelineStateIcon/PipelineStateIcon";
import { twMerge } from "tailwind-merge";
import { getBuildColor } from "~/components/build/Show/lib/getBuildColor";
import { CollapsedBuildActions } from "~/components/build/Show/components/CollapsedBuildActions";
import BuildNumber from "~/components/build/Header/BuildNumber";
import { PullRequest } from "~/components/build/Header/PullRequest";
import NewBuildUIToggleDialog from "~/components/build/Show/components/NewBuildUIToggleDialog";
import {
  clampSidebarWidth,
  useBuildPreferencesStore,
} from "~/components/build/Show/lib/useBuildPreferencesStore";
import { ResizeHandle } from "~/components/build/Show/components/ResizeHandle";
import { BuildPagePreferences } from "~/components/build/Show/components/BuildPagePreferences";
import classNames from "classnames";
import StepDrawer from "~/components/build/Show/components/StepDrawer";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { Revel } from "~/components/build/Show/components/Revel";
import debounce from "lodash/debounce";
import Icon from "~/components/shared/Icon";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { FilterByOption, useFilterStore } from "~/components/build/Show/lib/useFilterStore";
import {
  useAdjustDockPositionForContainer,
  DockPosition,
} from "~/components/build/Show/lib/useAdjustedDockPosition";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import { useFocusOnFailure } from "~/components/build/Show/components/StepDrawer/GoToFailure/useFocusOnFailure";
import { useHotkeys, useHotkeysContext } from "react-hotkeys-hook";
import { LogLineSelectionProvider } from "~/components/build/Show/lib/useLogLineSelection";
import { CurrentStepSelectionContextProvider } from "~/components/build/Show/lib/useCurrentJobOrStep";
import { useEnsureStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import useBuildFavicon from "~/components/build/Show/lib/useBuildFavicon";

const PANEL_TRANSITION = {
  type: "spring",
  duration: 0.2,
};

/**
 * The "classic" build layout, which is used when the new build UI is not enabled.
 *
 * @deprecated
 */
function ClassicBuildLayout() {
  const { build, setBuildData, reloadBuild } = useBuild();
  const matches = useMatches();
  const view = [...matches].pop()?.id as BuildView;

  return (
    <CurrentStepSelectionContextProvider>
      <PausedClusterQueuesNotice pausedQueues={build.dispatchPausedClusterQueues} />
      <Header
        build={build}
        showRebuild={true}
        setBuildData={setBuildData}
        reloadBuild={reloadBuild}
        currentView={view}
        showJobs={view === BuildView.JobList}
      />
      <div className="mb-4 flex justify-between border-b border-gray-400 px-3 pr-0">
        <BuildContentNavigation />
        <NewBuildUIToggleDialog optIn={true} />
      </div>

      <Outlet />
    </CurrentStepSelectionContextProvider>
  );
}

function NextFailureHandler() {
  const { focusOnFailure } = useFocusOnFailure();
  useHotkeys("f", focusOnFailure);

  return null;
}

/**
 * BBUI Header
 */
function BuildHeader(props: { hideSidebar: boolean }) {
  const { build } = useBuild();
  const theme = getBuildColor(build);

  return (
    <div data-testid="bbui-build-header" className="flex flex-wrap gap-x-2 gap-y-2">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-2 sm:flex-nowrap">
        {props.hideSidebar ? null : <ToggleSidebarButton />}
        <PipelineInfo />

        <div className="flex min-w-0 flex-initial shrink-[2] items-center text-sm">
          <span className="hidden shrink-0 select-none px-1 text-gray-500 sm:!block">/</span>

          <Branch {...build} className="min-w-8 max-w-40 px-2" />

          {build.commitId && (
            <>
              <span className="shrink-0 select-none px-1 text-gray-500">/</span>

              <Commit {...build} className="min-w-8 max-w-40 px-2" />

              {build.pullRequest && (
                <span className="shrink-0 pr-2">
                  (<PullRequest {...build} />)
                </span>
              )}
            </>
          )}

          <span className="shrink-0 select-none px-1 text-gray-500">/</span>

          <div className="flex shrink-0 items-center gap-1 px-2 sm:mr-2">
            <BuildNumber {...build} />
          </div>
        </div>
      </div>

      <div className="flex flex-auto items-center justify-between gap-4">
        <div className="inline-flex min-w-0 flex-initial items-center gap-2 text-sm">
          <div className="flex shrink-0 items-center gap-1.5">
            <PipelineStateIcon
              build={build}
              style={{
                width: "20px",
                height: "20px",
                color: theme.primaryColor,
              }}
              variant={VARIANTS.large}
            />
            <BuildDuration build={build} />
          </div>
        </div>

        <div className="flex min-h-[30px] min-w-0 items-center gap-1.5">
          <CollapsedBuildActions />
          <PipelineSettingsMenu />
        </div>
      </div>
    </div>
  );
}

/**
 * Build layout (aka the "BBUI")
 */
function BuildLayout() {
  const { build } = useBuild();

  const { sid, jid, open } = useQueryParams();
  const stepOrJobId = sid || jid;

  const widthRef = useRef(useBuildPreferencesStore.getState().sidebarWidth);
  const setWidth = useBuildPreferencesStore((state) => state.setSidebarWidth);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  useAdjustDockPositionForContainer("#drawer-container");
  const dockPosition = useBuildPreferencesStore((state) => state.dockPosition);

  const { enableScope, disableScope } = useHotkeysContext();

  /**
   * Update the hotkey scope when the drawer is active
   */
  useEffect(() => {
    if (stepOrJobId && open !== "false") {
      enableScope("drawer");
    } else {
      disableScope("drawer");
    }
  }, [stepOrJobId, open, enableScope, disableScope]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSyncWidth = useCallback(debounce(setWidth, 250), []);

  const setFilterBy = useFilterStore((state) => state.setFilterBy);

  /**
   * Auto-apply "Failed" filter when the server indicates the build has failures worth highlighting.
   */
  useEffect(() => {
    if (build.applyFailedFilter) {
      setFilterBy([FilterByOption.Failed]);
    }
  }, [build.applyFailedFilter, setFilterBy]);

  const resizeSidebar = useCallback(
    (x: number) => {
      if (!sidebarRef.current || !handleRef.current) {
        return;
      }

      const offset =
        handleRef.current.getBoundingClientRect().left -
        sidebarRef.current.getBoundingClientRect().width;

      const width = clampSidebarWidth(x - offset);
      sidebarRef.current.style.width = `${width}px`;
      debounceSyncWidth(width);
      widthRef.current = width;
    },
    [debounceSyncWidth],
  );

  const hideSidebar = Features.HideBuildSidebar || Features.BuildPageListView;
  const isSidebarOpen = useBuildPreferencesStore((state) =>
    hideSidebar ? false : state.isSidebarOpen,
  );
  const toggleSidebar = useBuildPreferencesStore((state) => state.toggleSidebar);

  useHotkeys("meta+b", toggleSidebar, { enabled: !hideSidebar });

  return (
    <CurrentStepSelectionContextProvider>
      <NextFailureHandler />
      <div className="relative -mt-2.5 flex min-w-0 flex-auto flex-col gap-2">
        <BuildHeader hideSidebar={hideSidebar} />

        <PausedClusterQueuesNotice pausedQueues={build.dispatchPausedClusterQueues} />

        <div className="flex min-w-0 flex-auto flex-row">
          <LayoutGroup>
            <AnimatePresence initial={false}>
              {isSidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, marginLeft: `-${widthRef.current}px` }}
                  animate={{ opacity: 1, marginLeft: 0 }}
                  exit={{ opacity: 0, marginLeft: `-${widthRef.current}px` }}
                  transition={PANEL_TRANSITION}
                  className="hidden min-w-0 flex-col gap-4 md:!flex"
                  style={{ width: `${widthRef.current}px` }}
                  ref={sidebarRef}
                >
                  <div className="relative mr-3 h-full">
                    <Sidebar />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div
              layout={true}
              id="drawer-container"
              transition={PANEL_TRANSITION}
              className={classNames(
                "@container/main relative flex min-w-0 flex-auto flex-row-reverse gap-2 md:min-w-[540px]",
                {
                  "flex-col-reverse": dockPosition === DockPosition.Bottom,
                },
              )}
            >
              {isSidebarOpen && (
                <ResizeHandle
                  direction="horizontal"
                  onResize={resizeSidebar}
                  ref={handleRef}
                  className="left-[1px] rounded-l-md"
                />
              )}
              {/* As an optimization, render the step drawer **before** the main outlet to handle cases with excessive DOM nodes */}
              {stepOrJobId && open !== "false" && <StepDrawer key={stepOrJobId} />}

              <div className="@container/outlet relative flex min-w-0 flex-auto flex-col rounded-md md:min-h-60 md:min-w-[540px]">
                <div className="flex min-w-0 flex-auto flex-col rounded-md border border-gray-500 bg-slate-100">
                  <div
                    className={twMerge(
                      "shadow-xs flex border-b border-gray-400 px-2",
                      classNames({ "pr-2": Features.BuildSidebarToggle }),
                    )}
                  >
                    <BuildContentNavigation />
                    <div className={classNames("ml-auto mr-1 flex items-center gap-2")}>
                      {!Features.HideUIToggle && !Features.PreviewToggleInSettings && (
                        <NewBuildUIToggleDialog optIn={false} />
                      )}
                      {!!Features.DefaultToAnnotationsTab && <BuildPagePreferences />}
                    </div>
                  </div>

                  <Outlet />
                </div>
              </div>
            </motion.div>
          </LayoutGroup>
        </div>
      </div>
    </CurrentStepSelectionContextProvider>
  );
}

const ToggleSidebarButton = () => {
  const track = useTrack();
  const isSidebarOpen = useBuildPreferencesStore((state) => state.isSidebarOpen);
  const toggleSidebar = useBuildPreferencesStore((state) => state.toggleSidebar);

  const handleClick = useCallback(() => {
    toggleSidebar();
    track("Build Sidebar Toggled", { state: isSidebarOpen ? "collapsed" : "expanded" });
  }, [toggleSidebar, isSidebarOpen, track]);

  let userAgentMetaIcon = "⌘";
  if (typeof window !== "undefined") {
    userAgentMetaIcon = navigator.userAgent.includes("Mac") ? "⌘" : "Ctrl";
  }

  const hotKey = `${userAgentMetaIcon}+B`;

  return (
    <button
      data-testid="sidebar-toggle"
      onClick={handleClick}
      title={isSidebarOpen ? `Collapse sidebar (${hotKey})` : `Expand sidebar (${hotKey})`}
      className="hidden h-6 w-6 items-center justify-center rounded-sm bg-transparent transition-colors hover:bg-purple-100 hover:text-purple-600 md:!flex"
    >
      <Icon
        icon="custom/outline/sidebar-left"
        className={classNames("h-[18px] w-[18px]", {
          "[&>path:nth-child(2)]:fill-current [&>path:nth-child(n+3)]:stroke-white": isSidebarOpen,
        })}
      />
    </button>
  );
};

function ModernBuildLayout() {
  const { build } = useBuild();
  const { sid, jid, open } = useQueryParams();
  const { enableScope, disableScope } = useHotkeysContext();

  const setFilterBy = useFilterStore((state) => state.setFilterBy);
  const dockPosition = useBuildPreferencesStore((state) => state.dockPosition);

  const stepOrJobId = sid || jid;

  useAdjustDockPositionForContainer("#drawer-container");

  /**
   * Update the hotkey scope when the drawer is active
   */
  useEffect(() => {
    if (stepOrJobId && open !== "false") {
      enableScope("drawer");
    } else {
      disableScope("drawer");
    }
  }, [stepOrJobId, open, enableScope, disableScope]);

  /**
   * Auto-apply "Failed" filter when the server indicates the build has failures worth highlighting.
   */
  useEffect(() => {
    if (build.applyFailedFilter) {
      setFilterBy([FilterByOption.Failed]);
    }
  }, [build.applyFailedFilter, setFilterBy]);

  return (
    <CurrentStepSelectionContextProvider>
      <NextFailureHandler />
      <div className="relative -mt-2.5 flex min-w-0 flex-auto flex-col gap-2">
        <BuildHeader hideSidebar={true} />

        <PausedClusterQueuesNotice pausedQueues={build.dispatchPausedClusterQueues} />

        <div className="flex min-w-0 flex-auto flex-col rounded-md border border-gray-400 bg-slate-100">
          <div className="flex flex-row items-center justify-between px-2">
            <BuildContentNavigation />
            <div className={classNames("ml-auto mr-1 flex items-center gap-2")}>
              {!Features.HideUIToggle && !Features.PreviewToggleInSettings && (
                <NewBuildUIToggleDialog optIn={false} />
              )}
            </div>
          </div>

          <div className="flex min-w-0 flex-auto flex-row">
            <div
              id="drawer-container"
              className={classNames(
                "@container/main relative m-1 flex min-w-0 flex-auto flex-row-reverse gap-1",
                {
                  "flex-col-reverse": dockPosition === DockPosition.Bottom,
                },
              )}
            >
              {/* As an optimization, render the step drawer **before** the main outlet to handle cases with excessive DOM nodes */}
              {stepOrJobId && open !== "false" && <StepDrawer key={stepOrJobId} />}

              <div className="@container/outlet relative flex min-w-0 flex-auto flex-col rounded-md md:min-h-60 md:min-w-[540px]">
                <div className="flex min-w-0 flex-auto flex-col overflow-hidden rounded-md border border-gray-500 bg-slate-100">
                  <Outlet />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CurrentStepSelectionContextProvider>
  );
}

export default function BuildShowLayout() {
  const { build } = useBuild();

  useEnsureStepDependencies(build);
  useBuildFavicon(build);

  // Modern Build Layout
  if (Features.BuildSidebar && Features.BuildPageDockedAnnotations) {
    return (
      <>
        <ModernBuildLayout />
        <Revel />
      </>
    );
  }

  // Better Build Layout
  if (Features.BuildSidebar) {
    return (
      <>
        <BuildLayout />
        <Revel />
      </>
    );
  }

  // Classic Experience Layout
  return (
    <LogLineSelectionProvider>
      <ClassicBuildLayout />
      <Revel />
    </LogLineSelectionProvider>
  );
}
